import React, { useEffect, useRef } from 'react';

// eslint-disable-next-line import/no-extraneous-dependencies
import Lottie, { type AnimationItem } from 'lottie-web';

import { fg } from '@atlaskit/platform-feature-flags/fg';

import shapeMorphAnimation from './assets/shape-morph-animation.json';

// Module-level state — persists the current frame across remounts
// so the morphing animation continues smoothly without restarting.
let savedFrame = 0;
let hasPlayedBefore = false;

const getSafeFrame = (frame: number): number => (Number.isFinite(frame) && frame >= 0 ? frame : 0);

export type MorphingShapeLoaderSize = 'small' | 'medium' | 'large';

const SIZE_MAP: Record<MorphingShapeLoaderSize, number> = {
	small: 16,
	medium: 24,
	large: 32,
};

type MorphingShapeLoaderProps = {
	testId?: string;
	size?: MorphingShapeLoaderSize;
};

export const MorphingShapeLoader = ({
	testId,
	size = 'small',
}: MorphingShapeLoaderProps): JSX.Element => {
	const containerRef = useRef<HTMLDivElement | null>(null);

	const lottieSize = fg('rovo_morphing_shape_loader_size') ? SIZE_MAP[size] : 16;
	const containerSize = fg('rovo_morphing_shape_loader_size') ? lottieSize + 8 : 24;

	useEffect(() => {
		const currentRef = containerRef.current;
		if (!currentRef) {
			return;
		}

		const baseAnimationConfig = {
			container: currentRef,
			animationData: shapeMorphAnimation,
			renderer: 'svg' as const,
			loop: true,
		};

		if (typeof Lottie.loadAnimation !== 'function') {
			return;
		}

		const animationInstance: AnimationItem = Lottie.loadAnimation({
			...baseAnimationConfig,
			autoplay: true,
		});

		if (hasPlayedBefore) {
			// Remount — resume from saved frame, while still autoplaying as a visible fallback.
			animationInstance.goToAndPlay(getSafeFrame(savedFrame), true);
		}

		hasPlayedBefore = true;

		return () => {
			// Save current frame before destroying
			savedFrame = getSafeFrame(animationInstance.currentFrame);
			animationInstance.destroy();
		};
	}, []);

	const containerStyle: React.CSSProperties = {
		width: containerSize,
		height: containerSize,
		display: 'flex',
		alignItems: 'center',
		justifyContent: 'center',
		overflow: 'visible',
	};

	const lottieWrapperStyle: React.CSSProperties = {
		width: lottieSize,
		height: lottieSize,
	};

	return (
		// eslint-disable-next-line @atlaskit/ui-styling-standard/enforce-style-prop -- static styles for lottie container
		<div style={containerStyle} data-testid={testId}>
			{/* eslint-disable-next-line @atlaskit/ui-styling-standard/enforce-style-prop -- static styles for lottie wrapper */}
			<div style={lottieWrapperStyle} ref={containerRef} />
		</div>
	);
};
